import { useAuth, useUser } from '@myosh/myosh-login'
import {
  AboutInfoModal,
  DropDown,
  type DropDownNoSelection,
  type DropDownResult,
  isDropDownResult,
  OdinIcon,
  OdinIconSize,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  type TypeOrArray
} from '@myosh/odin-components'
import type { MenuItem } from 'primereact/menuitem'
import type { TieredMenu as PrimeReactMenu } from 'primereact/tieredmenu'
import type { MouseEvent } from 'react'
import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react'
import filterIcon from '../assets/icons/filter.svg'
import menuIcon from '../assets/icons/menu.svg'
import moreIcon from '../assets/icons/more.svg'
import { useExportExcel } from '../mixins/useExportExcel'
import { useGapAnalysisViews } from '../mixins/useGapAnalysisViews'
import { useTrainingMatrixViews } from '../mixins/useTrainingMatrixViews'
import { useGAViewsManagement, useTMViewsManagement } from '../mixins/viewsManagement'
import { gapAnalysisActions, trainingMatrixActions } from '../redux/actions'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import DisplayOptionsGA from './layout/gap-analysis/display-options'
import FilterOptionsGA from './layout/gap-analysis/filter-options'
import HierarchyOptionsGA from './layout/gap-analysis/hierarchy-options'
import GapAnalysisViewManagementModal from './layout/training-management/ga-view-management-modal'
import ScheduledReportList from './layout/training-management/scheduled-reports/ScheduledReportList'
import TrainingMatrixViewManagementModal from './layout/training-management/tm-view-management-modal'
import ComplianceOptionsTM from './layout/training-matrix/compliance-options'
import DisplayOptionsTM from './layout/training-matrix/display-options'
import FilterOptionsTM from './layout/training-matrix/filter-options'
import HierarchyOptionsTM from './layout/training-matrix/hierarchy-options'
import { Page, XLG_DROPDOWN_DATA } from './layout/training-matrix/view_dropdown_data'
import Dropdown from './UI/dropdown/Dropdown'
import FilterTabs from './UI/filter-tabs'
import { IconButton } from './UI/icon-button'
import LoadingSpinner from './UI/loading-spinner'
import Menu from './UI/menu/Menu'
import type { DropdownOption } from './UI/multi-select-dropdown/MultiSelectDropdown'
import PermissionCheck from './UI/permission-check/PermissionCheck'

const TrainingMatrix = React.lazy(() => import('./layout/training-matrix'))
const GapAnalysis = React.lazy(() => import('./layout/gap-analysis'))

export interface GapUser {
  id: string
  fullName: string
  occupation: string
  affiliation?: string
  secondaryOccupations?: Array<string>
}

export interface GapUserTrainings {
  requiredTrainings: Array<string>
  completedTrainings?: Array<string>
  uncompletedTrainings?: Array<string>
}

function TmsApp() {
  const { logout } = useAuth()
  const { state: userState } = useUser()
  const dispatch = useAppDispatch()
  const { handleSelectGAView } = useGAViewsManagement()
  const { handleSelectTMView } = useTMViewsManagement()

  // State:
  // Local state
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)
  const [showScheduleExportModal, setShowScheduleExportModal] = useState(false)
  const [selectedView, setSelectedView] = useState<DropDownResult>({
    value: XLG_DROPDOWN_DATA[0].id,
    text: XLG_DROPDOWN_DATA[0].text
  })

  const menuRef = useRef<PrimeReactMenu>(null)
  const moreActionsMenuRef = useRef<PrimeReactMenu>(null)

  // Async state
  const trainingMatrixState = useAppSelector(state => state.trainingMatrix)
  const gapAnalysisState = useAppSelector(state => state.gapAnalysis)

  const {
    views: trainingMatrixViews,
    isFetching: isFetchingTrainingMatrixViews,
    isError: hasTrainingMatrixViewsError,
    error: trainingMatrixViewsError
  } = useTrainingMatrixViews(selectedView.value === Page.TrainingMatrix)
  const {
    views: gapAnalysisViews,
    isFetching: isFetchingGapAnalysisViews,
    isError: hasGapAnalysisViewsError,
    error: gapAnalysisViewsError
  } = useGapAnalysisViews(selectedView.value === Page.GapAnalysis)

  const { handleFetchExport } = useExportExcel()

  // Handlers
  const hasViewsPermissions = () => {
    if (selectedView.value === Page.TrainingMatrix) {
      if (isFetchingTrainingMatrixViews) return false

      return !hasTrainingMatrixViewsError || trainingMatrixViewsError?.data?.status !== 403
    } else if (selectedView.value === Page.GapAnalysis) {
      if (isFetchingGapAnalysisViews) return false

      return !hasGapAnalysisViewsError || gapAnalysisViewsError?.data?.status !== 403
    }

    return false
  }

  const handleViewChange = (selection: TypeOrArray<DropDownResult> | DropDownNoSelection | undefined) => {
    if (isDropDownResult(selection)) {
      setSelectedView(selection)
    }
  }

  const handleExportButtonClick = useCallback(
    () => handleFetchExport(Number(selectedView.value)),
    [selectedView.value, handleFetchExport]
  )

  const handleScheduleExportModal = useCallback(() => setShowScheduleExportModal(prev => !prev), [])

  const handleSelectView = useCallback(
    (selection: DropdownOption | undefined) => {
      selectedView.value === Page.TrainingMatrix ? handleSelectTMView(selection) : handleSelectGAView(selection)
    },
    [selectedView.value, handleSelectTMView, handleSelectGAView]
  )

  const handleCloseAboutInfoModal = () => {
    dispatch(trainingMatrixActions.setShowAboutInfoModal(false))
  }

  const handleLogout = () => logout()

  const handleMoreMenuButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    moreActionsMenuRef.current?.toggle(event)
  }

  const modalTabsTM = useMemo(
    () => [
      {
        title: 'Filter',
        view: <FilterOptionsTM />
      },
      {
        title: 'Display',
        view: <DisplayOptionsTM />
      },
      {
        title: 'Compliance',
        view: <ComplianceOptionsTM />
      },
      {
        title: 'Hierarchy',
        view: <HierarchyOptionsTM />
      }
    ],
    []
  )

  const modalTabsGA = useMemo(
    () => [
      {
        title: 'Filter',
        view: <FilterOptionsGA />
      },
      {
        title: 'Display',
        view: <DisplayOptionsGA />
      },
      {
        title: 'Hierarchy',
        view: <HierarchyOptionsGA />
      }
    ],
    []
  )

  const moreActionsMenuItems = useMemo(
    () =>
      [
        {
          label: 'Restore Current View',
          icon: <OdinIcon icon='Undo2' />,
          command: () => {
            const currentSelectedView =
              selectedView.value === Page.TrainingMatrix
                ? trainingMatrixState.selectedView
                : gapAnalysisState.selectedView

            handleSelectView(currentSelectedView)
          }
        },
        {
          label: 'Save Current View',
          icon: <OdinIcon icon='Save' />,
          command: () => {
            dispatch(
              selectedView.value === Page.TrainingMatrix
                ? trainingMatrixActions.setShowSaveViewModal(true)
                : gapAnalysisActions.setShowSaveViewModal(true)
            )
          }
        },
        {
          label: 'Manage Views',
          icon: <OdinIcon icon='Columns3Cog' />,
          command: () => {
            dispatch(
              selectedView.value === Page.TrainingMatrix
                ? trainingMatrixActions.setShowManageViewModal(true)
                : gapAnalysisActions.setShowManageViewModal(true)
            )
          }
        },
        { separator: true },
        {
          label: 'Export',
          icon: <OdinIcon icon='Download' />,
          items: [
            {
              label: 'Excel',
              command: handleExportButtonClick
            }
          ]
        },
        {
          label: 'Scheduled Exports',
          icon: <OdinIcon icon='Calendar' />,
          command: handleScheduleExportModal
        }
      ] as Array<MenuItem>,
    [
      selectedView.value,
      trainingMatrixState.selectedView,
      gapAnalysisState.selectedView,
      dispatch,
      handleExportButtonClick,
      handleScheduleExportModal,
      handleSelectView
    ]
  )

  const menuItems = useMemo(
    () =>
      [
        {
          label: 'About',
          icon: <OdinIcon icon='Info' />,
          command: () => dispatch(trainingMatrixActions.setShowAboutInfoModal(true))
        }
      ] as Array<MenuItem>,
    [dispatch]
  )

  const viewsList = () => {
    if (selectedView.value === Page.TrainingMatrix && trainingMatrixViews && !isFetchingTrainingMatrixViews) {
      return trainingMatrixViews.map(view => {
        return { value: view.id || '', text: view.title || '' }
      })
    }

    if (selectedView.value === Page.GapAnalysis && gapAnalysisViews && !isFetchingGapAnalysisViews) {
      return gapAnalysisViews.map(view => {
        return { value: view.id || '', text: view.title || '' }
      })
    }

    return []
  }

  return (
    <main>
      <div className='training-matrix__header'>
        <div>
          <DropDown
            allowClear={false}
            data={XLG_DROPDOWN_DATA}
            label={''}
            onChange={handleViewChange}
            setDefaultValue={true}
            textField='text'
            valueField='id'
            value={selectedView}
            allowSearch={false}
            dropDownStyles='odin-dropdown'
            selectorStyles='odin-dropdown__selector'
          />
        </div>
        <div className='training-matrix__header-buttons' hidden={!hasViewsPermissions()}>
          <div className='mr-4 pt-1'>
            <Dropdown
              options={viewsList()}
              selectedOption={
                selectedView.value === Page.TrainingMatrix
                  ? trainingMatrixState.selectedView
                  : gapAnalysisState.selectedView
              }
              onChange={handleSelectView}
              label='View'
              placeholder='Select view'
              isLoading={isFetchingTrainingMatrixViews || isFetchingGapAnalysisViews}
            />
          </div>

          <Popover open={filterPopoverOpen} onOpenChange={open => setFilterPopoverOpen(open)}>
            <PopoverTrigger className='icon-button'>
              <img src={filterIcon} alt='icon-button' className='icon-button__icon' />
            </PopoverTrigger>
            <PopoverContent side='bottom' align='start' sideOffset={8} className='relative w-auto min-w-[350px] p-4'>
              <PopoverClose
                className='absolute top-2 right-2 cursor-pointer border-none bg-transparent p-1'
                aria-label='Close'>
                <OdinIcon icon='X' size={OdinIconSize.ExtraSmall} />
              </PopoverClose>
              <FilterTabs
                tabs={
                  selectedView.value === Page.TrainingMatrix
                    ? modalTabsTM
                    : selectedView.value === Page.GapAnalysis
                      ? modalTabsGA
                      : []
                }
              />
            </PopoverContent>
          </Popover>

          <IconButton src={moreIcon} onClick={handleMoreMenuButtonClick} />
          <Menu ref={moreActionsMenuRef} items={moreActionsMenuItems} />

          <div style={{ marginLeft: 'auto', paddingRight: '0.5rem' }}>
            <IconButton src={menuIcon} onClick={event => menuRef.current?.toggle(event)} />
            <Menu ref={menuRef} items={menuItems} />
          </div>
        </div>
      </div>
      <PermissionCheck
        hasPermission={hasViewsPermissions()}
        isLoading={isFetchingTrainingMatrixViews || isFetchingGapAnalysisViews}>
        <Suspense fallback={<LoadingSpinner />}>
          {selectedView.value === Page.TrainingMatrix && <TrainingMatrix />}
          {selectedView.value === Page.GapAnalysis && <GapAnalysis />}
        </Suspense>
      </PermissionCheck>
      {trainingMatrixState.showAboutInfoModal && (
        <AboutInfoModal
          version={process.env.REACT_APP_VERSION}
          currentSchema={userState.user?.currentSchema ? { text: userState.user?.currentSchema } : undefined}
          isAboutInfoOpen={trainingMatrixState.showAboutInfoModal}
          showLogoutButton={userState.isDefaultIdp}
          onClose={handleCloseAboutInfoModal}
          onLogout={handleLogout}
        />
      )}
      {selectedView.value === Page.TrainingMatrix && <TrainingMatrixViewManagementModal />}
      {selectedView.value === Page.GapAnalysis && <GapAnalysisViewManagementModal />}
      {showScheduleExportModal && (
        <ScheduledReportList selectedPage={selectedView.value as Page} hide={handleScheduleExportModal} />
      )}
    </main>
  )
}

export default TmsApp
